
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

export interface AdminUser {
  id: string | number;
  username: string;
  name?: string;
  real_name?: string;
  email?: string;
  avatar?: string | null;
  gender?: string | null;
  role: 'super_admin' | 'admin' | 'editor' | 'operator';
  permissions: string[];
}

function normalizeAdminUser(raw: any): AdminUser {
  return {
    ...raw,
    name: raw.name || raw.real_name || raw.username || '',
    permissions: Array.isArray(raw.permissions) ? raw.permissions : [],
  };
}

function readStoredAuth(): { token: string | null; user: AdminUser | null } {
  try {
    const token = localStorage.getItem('adminToken');
    const userStr = localStorage.getItem('adminUser');
    if (!token || !userStr) {
      return { token: null, user: null };
    }

    // 检查 token 是否过期
    const tokenParts = token.split('.');
    if (tokenParts.length === 3) {
      const payload = JSON.parse(atob(tokenParts[1]));
      const now = Math.floor(Date.now() / 1000);
      if (payload.exp && payload.exp < now) {
        localStorage.removeItem('adminToken');
        localStorage.removeItem('adminUser');
        return { token: null, user: null };
      }
    }

    return { token, user: normalizeAdminUser(JSON.parse(userStr)) };
  } catch (error) {
    console.error('Parse admin auth error:', error);
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminUser');
    return { token: null, user: null };
  }
}

export const useAuth = () => {
  // 同步从 localStorage 初始化，避免登录后跳转瞬间被判定为未授权
  const initialAuth = useMemo(() => readStoredAuth(), []);
  const [user, setUser] = useState<AdminUser | null>(initialAuth.user);
  const [loading, setLoading] = useState(false);
  const [token, setToken] = useState<string | null>(initialAuth.token);
  const navigate = useNavigate();

  const logout = useCallback(() => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminUser');
    sessionStorage.removeItem('adminRedirectPath');
    setUser(null);
    setToken(null);
  }, []);

  // 检查认证状态的函数
  const checkAuthStatus = useCallback(() => {
    const { token: tokenVal, user: userData } = readStoredAuth();
    setUser(userData);
    setToken(tokenVal);
    return !!(tokenVal && userData);
  }, []);

  useEffect(() => {
    checkAuthStatus();
    setLoading(false);
  }, [checkAuthStatus]);

  // 强制登出并跳转到登录页面
  const forceLogout = useCallback((reason?: string) => {
    logout();
    const currentPath = window.location.pathname + window.location.search;
    if (currentPath.startsWith('/admin') && currentPath !== '/admin/login') {
      sessionStorage.setItem('adminRedirectPath', currentPath);
    }
    
    const params = new URLSearchParams();
    if (reason) params.set('reason', reason);
    
    navigate(`/admin/login${params.toString() ? '?' + params.toString() : ''}`, { replace: true });
  }, [logout, navigate]);

  const hasPermission = useCallback((permission: string): boolean => {
    if (!user) return false;
    if (user.role === 'super_admin' || user.permissions.includes('all') || user.permissions.includes('*')) return true;
    return user.permissions.includes(permission);
  }, [user]);

  const isAuthenticated = useCallback((): boolean => {
    const tokenVal = localStorage.getItem('adminToken');
    if (!tokenVal || !user) return false;
    
    // 检查token是否过期
    try {
      const tokenParts = tokenVal.split('.');
      if (tokenParts.length === 3) {
        const payload = JSON.parse(atob(tokenParts[1]));
        const now = Math.floor(Date.now() / 1000);
        
        if (payload.exp && payload.exp < now) {
          console.warn('Token已过期，清除本地状态');
          logout();
          return false;
        }
      }
    } catch (error) {
      console.warn('Token解析失败，清除本地状态');
      logout();
      return false;
    }
    
    return true;
  }, [user, logout]);

  // 检查是否需要重新认证（用于API调用失败后）
  const requireAuth = useCallback(() => {
    if (!isAuthenticated()) {
      forceLogout('session_expired');
      return false;
    }
    return true;
  }, [isAuthenticated, forceLogout]);

  return {
    user,
    loading,
    token,
    logout,
    forceLogout,
    hasPermission,
    isAuthenticated,
    requireAuth,
    checkAuthStatus
  };
};
